/*
 * File: MomentaryAssessment.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 22, 2025 at 4:11 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

// Utilities
import { toastSuccess } from '../../utilities/toaster';

// Services
import { fetchMomentaryAssessments, submitMomentaryAssessmentResponse } from '../../services/momentaryAssessment';

// Components
import { Typography } from '../Typography';

// Constants
import { AVAILABLE_I18N_NAMESPACES, MOMENTARY_ASSESSMENT_QUESTION_TYPES, SOURCES } from '../../../constants/base.constants';

// Styles
import * as S from './MomentaryAssessment.styles';


/**
 * Component
 */

export const MomentaryAssessment = ({
	className, source, onLoad, onCompletion
}) => {

	// Create state handlers
	const [assessments, setAssessments] = useState([]);
	const [answerManifest, setAnswerManifest] = useState([]);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Get actions from hooks
	const { executeRecaptcha } = useGoogleReCaptcha();

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Fetch momentary assessment
	const prepareAssessment = async () => {
		try {

			// Fetch momentary assessments
			const { data: { result: { assessments: momentaryAssessments } } } = await fetchMomentaryAssessments({ source });

			// Set assessments
			setAssessments(momentaryAssessments);

			// Fire on load function
			if (onLoad) onLoad(momentaryAssessments.length > 0);

		} catch (e) {

			// Fire on load function
			if (onLoad) onLoad(false);
		}
	};

	// Handle answer
	const handleAnswer = async ({ assessmentId, questionId, answerId }) => {

		// Check if answer already selected
		if (!answerManifest.some((answer) => answer.assessmentId === assessmentId && answer.questionId === questionId && answer.answerId === answerId)) {

			// Get answer object
			const questionObj = assessments
				.find((assessment) => assessment.assessment_id === assessmentId).questions
				.find((question) => question.question_id === questionId);

			// Filter if necessary
			let answers = [...answerManifest];
			if (questionObj.question_type === MOMENTARY_ASSESSMENT_QUESTION_TYPES.POLAR) {
				answers = answers.filter((answer) => !(answer.assessmentId === assessmentId && answer.questionId === questionId));
			}

			// Update answer manifest
			const manifest = [...answers, { assessmentId, questionId, answerId }];

			// Update UI
			setAnswerManifest(manifest);

			// Create payload
			const params = { assessmentId, questionId, answerId };

			// Add reCAPTCHA token
			try { params.recaptchaToken = await executeRecaptcha('submit_momentary_assessment'); } catch (_) {}

			// Submit response
			await submitMomentaryAssessmentResponse(params);

			// Check if all questions answered
			const allQuestionsAnswered = assessments.every((assessment) => assessment.questions.every((question) => manifest.some((a) => a.assessmentId === assessment.assessment_id && a.questionId === question.question_id)));
			if (allQuestionsAnswered) {

				// Show confirmation message
				toastSuccess(uiMode, partner, t('momentaryAssessment.confirmation'));

				// Set timeout for completion
				setTimeout(() => {

					// Fire on completion
					if (onCompletion) onCompletion();

				}, [2000]);
			}
		}
	};

	// Handle actions on component load
	useEffect(() => {

		// Prepare assessment
		prepareAssessment();

	}, []);

	// Render component
	return (
		<S.Wrapper className={className}>

			{/* Instructions */}
			<S.InstructionsContainer>
				<Typography tag="p" variation="2">
					<Trans t={t} i18nKey="momentaryAssessment.instructions">
						We are focused on building the best experience possible for survivors. If you&apos;re comfortable, answer the following questions to help us improve the platform.
					</Trans>
				</Typography>
			</S.InstructionsContainer>

			{/* Assessments */}
			<S.AssessmentsContainer>
				{assessments.map((assessment) => (
					<S.QuestionsContainer key={assessment.assessment_id}>
						{assessment.questions.map((question) => (
							<S.Question key={question.question_id}>
								<Typography
									tag="p"
									variation="2"
									weight="semibold"
								>
									{question.question_locales[locale.localeBaseId]}
								</Typography>
								{question.question_type === MOMENTARY_ASSESSMENT_QUESTION_TYPES.POLAR ? (
									<S.Answers>
										{question.answers.map((answer) => {
											const isActive = answerManifest.find((item) => item.assessmentId === assessment.assessment_id && item.questionId === question.question_id && item.answerId === answer.answer_id);
											if (answer.is_positive) {
												return (
													<S.PositiveAnswerCard
														className={isActive ? 'active animate' : 'animate'}
														key={answer.answer_id}
														onClick={() => {
															handleAnswer({
																assessmentId: assessment.assessment_id,
																questionId: question.question_id,
																answerId: answer.answer_id
															});
														}}
													>
														<FontAwesomeIcon icon={['fas', 'check']} />
														<Typography variation="2" weight="medium" center>{answer.answer_locales[locale.localeBaseId]}</Typography>
													</S.PositiveAnswerCard>
												);
											}
											return (
												<S.NegativeAnswerCard
													className={isActive ? 'active animate' : 'animate'}
													key={answer.answer_id}
													onClick={() => {
														handleAnswer({
															assessmentId: assessment.assessment_id,
															questionId: question.question_id,
															answerId: answer.answer_id
														});
													}}
												>
													<FontAwesomeIcon icon={['fas', 'times']} />
													<Typography variation="2" weight="medium" center>{answer.answer_locales[locale.localeBaseId]}</Typography>
												</S.NegativeAnswerCard>
											);
										})}
									</S.Answers>
								) : (
									<S.Answers>
										{question.answers.map((answer) => {
											const isActive = answerManifest.find((item) => item.assessmentId === assessment.assessment_id && item.questionId === question.question_id && item.answerId === answer.answer_id);
											return (
												<S.NeutralAnswerCard
													className={isActive ? 'active animate' : 'animate'}
													key={answer.answer_id}
													onClick={() => {
														handleAnswer({
															assessmentId: assessment.assessment_id,
															questionId: question.question_id,
															answerId: answer.answer_id
														});
													}}
												>
													{answer.icon && <FontAwesomeIcon icon={answer.icon} />}
													<Typography variation="2" weight="medium" center>{answer.answer_locales[locale.localeBaseId]}</Typography>
												</S.NeutralAnswerCard>
											);
										})}
									</S.Answers>
								)}
							</S.Question>
						))}
					</S.QuestionsContainer>
				))}
			</S.AssessmentsContainer>
		</S.Wrapper>
	);
};


/**
 * Configuration
 */

MomentaryAssessment.displayName = 'MomentaryAssessment';
MomentaryAssessment.propTypes = {
	className: PropTypes.string,
	source: PropTypes.oneOf(Object.values(SOURCES)),
	onCompletion: PropTypes.func,
	onLoad: PropTypes.func
};
MomentaryAssessment.defaultProps = {
	className: null,
	source: null,
	onCompletion: null,
	onLoad: null
};
